import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { findSmartPickByQuery, isPlatformVerifiedListing, formatListingPublishedAt, isListingReserved, reservationBlocksBuyer, type SellerPublicSummary } from '@redevicer/shared'
import ListingCardSellerLine from './ListingCardSellerLine'
import { listingsAPI } from '../api/client'
import { useCompare } from '../hooks/useCompare'
import { useFavorites } from '../hooks/useFavorites'
import OfferModal from './OfferModal'
import ExchangeOfferModal from './ExchangeOfferModal'
import ListingCardVerifiedBadge from './ListingCardVerifiedBadge'
import CompareBar from './CompareBar'
import CompareModal from './CompareModal'
import CatalogBrowser from './CatalogBrowser'
import type { BrowsePreset } from './MarketplaceHome'
import type { CatalogFilterState } from '../hooks/useCatalogFilters'
import { listingCardPhotoUrls } from '../utils/listingThumb'
import ListingCardPhotoCarousel from './ListingCardPhotoCarousel'
import ListingFavoriteButton from './ListingFavoriteButton'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'
import { prefetchListing } from '../utils/listingDetailCache'
import { listingPublicPath } from '../utils/listingPath'
import { blocksOwnListingAction } from '../utils/testDeals'
import ListingCardSoldOverlay, { listingCardSoldCoverClass } from './ListingCardSoldOverlay'
import ListingCardBatteryBadge from './ListingCardBatteryBadge'

function isStorageToken(token: string): boolean {
  return /^\d+(gb|tb)$/i.test(token)
}

/** Drop 128GB / 1TB tokens — storage is filtered client-side so catalog counts stay available. */
function stripStorageFromQuery(query: string): string {
  return query
    .trim()
    .split(/\s+/)
    .filter((t) => !isStorageToken(t))
    .join(' ')
}

function listingMatchesQuery(hay: string, query: string): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return true
  const storageTokens = tokens.filter(isStorageToken)
  const coreTokens = tokens.filter((t) => !isStorageToken(t))
  if (!coreTokens.every((tok) => hay.includes(tok))) return false
  if (storageTokens.length === 0) return true
  const hayHasStorage = /\d+\s*(gb|tb)/i.test(hay)
  if (!hayHasStorage) return false
  return storageTokens.every((tok) => hay.includes(tok))
}

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url?: string
  is_primary: boolean
}

interface Listing {
  id: number
  title: string
  price: number
  condition: string
  in_box_completeness?: string
  battery_health?: number
  parts_authenticity?: Record<string, { status?: string; grade?: string | null }> | null
  photo_quality?: string | null
  photo_suggested_condition?: string | null
  model_match?: string | null
  usage_duration?: string | null
  has_factory_warranty?: boolean
  warranty_remaining?: string | null
  shipment_method?: string
  imei: string
  imei_verified?: boolean
  ai_recommended_eur?: number | null
  catalog_rank_group?: number
  created_at: string
  owner_id: number
  is_sold?: boolean
  is_reserved?: boolean
  reserved_for_user_id?: number | null
  seller_city?: string | null
  seller?: SellerPublicSummary | null
  phone_model: { manufacturer: string; model_name: string; hero_image_path?: string | null; release_year?: number | null }
  images?: ListingImage[]
}

interface ListingCatalogProps {
  token: string | null
  currentUserId?: number
  catalogPreset: BrowsePreset | null
  catalogPresetTick: number
  filters: CatalogFilterState
  onFiltersPatch: (next: Partial<CatalogFilterState>) => void
  onClearFilters: () => void
  hasActiveFilters: boolean
  /** Guest action needs login; optional intent resumes offer/exchange after auth */
  onRequireLogin?: (intent?: 'offer' | 'exchange' | null, listingId?: number) => void
  onOpenListingDetail?: (listingId: number) => void
  /** Immediate purchase at asking price */
  onBuyNow?: (listingId: number) => void
  onClearCatalogPreset?: () => void
  onGoToAccount?: () => void
}

export default function ListingCatalog({
  token,
  currentUserId,
  catalogPreset,
  catalogPresetTick,
  filters,
  onFiltersPatch,
  onClearFilters,
  hasActiveFilters,
  onRequireLogin,
  onOpenListingDetail,
  onBuyNow,
  onClearCatalogPreset,
  onGoToAccount,
}: ListingCatalogProps) {
  const { t, i18n } = useTranslation()
  const { has, toggle } = useFavorites()
  const { has: hasCompare, toggle: toggleCompare, max: maxCompare } = useCompare()
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(false)
  const [catalogError, setCatalogError] = useState('')
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null)
  const [exchangeListing, setExchangeListing] = useState<Listing | null>(null)
  const [compareOpen, setCompareOpen] = useState(false)

  function loadListings(searchQ = filters.q, city = filters.city) {
    setLoading(true)
    setCatalogError('')
    const apiQ = stripStorageFromQuery(searchQ)
    listingsAPI
      .getListings(0, 120, undefined, {
        q: apiQ.trim() || undefined,
        city: city.trim() || undefined,
      })
      .then((res) => setListings(res.data))
      .catch((err) => {
        setListings([])
        setCatalogError(err.response?.data?.detail || t('catalog.loadError'))
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadListings(filters.q, filters.city)
    }, filters.q.trim() ? 280 : 0)
    return () => window.clearTimeout(timer)
  }, [filters.q, filters.city])

  const activePresetQ = catalogPreset?.q?.trim() ?? ''
  const activeSmartPick = activePresetQ ? findSmartPickByQuery(activePresetQ) : undefined
  const activeFilterLabel = activeSmartPick
    ? t(activeSmartPick.titleKey)
    : activePresetQ || null

  function clearSearchFilter() {
    onFiltersPatch({ q: '' })
    onClearCatalogPreset?.()
  }

  function clearAllFilters() {
    onClearFilters()
    onClearCatalogPreset?.()
  }

  const filteredListings = listings
    .filter((listing) => {
      if (Number(listing.price) <= 0) return false
      const hay = `${listing.phone_model.manufacturer} ${listing.phone_model.model_name} ${listing.title} ${listing.seller_city ?? ''}`.toLowerCase()
      const searchMatches = listingMatchesQuery(hay, filters.q)
      const cityMatches = !filters.city || listing.seller_city === filters.city
      const conditionMatches = !filters.condition || listing.condition === filters.condition
      const warrantyMatches =
        !filters.warranty ||
        (filters.warranty === 'yes' && !!listing.has_factory_warranty) ||
        (filters.warranty === 'no' && !listing.has_factory_warranty)
      const usageMatches = !filters.usage || listing.usage_duration === filters.usage
      const batteryMatches =
        !filters.batteryMin ||
        (listing.battery_health != null && listing.battery_health >= parseInt(filters.batteryMin, 10))
      const inBoxMatches = !filters.inBox || listing.in_box_completeness === filters.inBox
      const priceMin = filters.priceMin.trim() ? parseFloat(filters.priceMin) : null
      const priceMax = filters.priceMax.trim() ? parseFloat(filters.priceMax) : null
      const priceMatches =
        (priceMin == null || !Number.isFinite(priceMin) || listing.price >= priceMin) &&
        (priceMax == null || !Number.isFinite(priceMax) || listing.price <= priceMax)
      const releaseYear = listing.phone_model.release_year ?? null
      const yearFrom = filters.yearFrom.trim() ? parseInt(filters.yearFrom, 10) : null
      const yearTo = filters.yearTo.trim() ? parseInt(filters.yearTo, 10) : null
      const yearMatches =
        releaseYear == null ||
        ((yearFrom == null || !Number.isFinite(yearFrom) || releaseYear >= yearFrom) &&
          (yearTo == null || !Number.isFinite(yearTo) || releaseYear <= yearTo))
      return (
        searchMatches &&
        cityMatches &&
        conditionMatches &&
        warrantyMatches &&
        usageMatches &&
        batteryMatches &&
        inBoxMatches &&
        priceMatches &&
        yearMatches
      )
    })
    .sort((a, b) => {
      if (filters.sortBy === 'price_asc') return a.price - b.price
      if (filters.sortBy === 'price_desc') return b.price - a.price
      if (filters.sortBy === 'model')
        return `${a.phone_model.manufacturer} ${a.phone_model.model_name}`.localeCompare(
          `${b.phone_model.manufacturer} ${b.phone_model.model_name}`,
        )
      return b.id - a.id
    })

  const isOwnListing = (listing: Listing) =>
    currentUserId !== undefined && listing.owner_id === currentUserId
  const isOwnBlocked = (listing: Listing) => blocksOwnListingAction(listing.owner_id, currentUserId)

  return (
    <section className="listing-catalog listing-catalog--simple">
      {activeFilterLabel ? (
        <div className="catalog-active-chip">
          <span className="catalog-active-chip-label">
            {t('vc.catalog.activeFilter')}: <strong>{activeFilterLabel}</strong>
          </span>
          <button type="button" className="catalog-active-chip-clear" onClick={clearSearchFilter}>
            {t('vc.catalog.clearFilter')}
          </button>
        </div>
      ) : null}

      <CatalogBrowser
        className="catalog-browser--connected"
        listings={listings}
        onPick={(q) => onFiltersPatch({ q })}
        onClear={clearAllFilters}
        activeQuery={filters.q}
        presetBrand={catalogPreset?.q ?? null}
        presetTick={catalogPresetTick}
      />

      {catalogError ? (
        <div className="catalog-empty-block catalog-error-block">
          <p className="catalog-error-block__title">{catalogError}</p>
          <p className="catalog-error-block__hint">{t('catalog.loadErrorHint')}</p>
          <button type="button" className="catalog-empty-clear catalog-error-block__retry" onClick={() => loadListings()}>
            {t('catalog.refresh')}
          </button>
        </div>
      ) : null}

      {!loading && !catalogError && filteredListings.length === 0 && (
        <div className="catalog-empty-block">
          <p className="catalog-empty">{t('catalog.empty')}</p>
          {listings.length > 0 && hasActiveFilters ? (
            <p className="catalog-empty-hint">{t('catalog.emptyHint', { total: listings.length })}</p>
          ) : null}
          {hasActiveFilters ? (
            <button type="button" className="catalog-empty-clear" onClick={clearAllFilters}>
              {t('catalog.clearAllFilters')}
            </button>
          ) : null}
        </div>
      )}

      <div className="listings-grid">
        {filteredListings.map((listing) => {
          const cardPhotos = listingCardPhotoUrls(listing)
          return (
          <article
            key={listing.id}
            className={`listing-card listing-card--clickable${listing.is_sold ? ' listing-card--sold' : ''}`}
          >
            <div
              className={listingCardSoldCoverClass(
                `listing-card-cover listing-card-hit${cardPhotos.isUserPhoto ? ' listing-card-cover--user' : ''}`,
                listing.is_sold,
              )}
              tabIndex={0}
              onMouseEnter={() => prefetchListing(listing.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onOpenListingDetail?.(listing.id)
                }
              }}
            >
              <ListingCardPhotoCarousel
                urls={cardPhotos.urls}
                isUserPhoto={cardPhotos.isUserPhoto}
                onTap={() => onOpenListingDetail?.(listing.id)}
                onPrefetch={() => prefetchListing(listing.id)}
              />
              <ListingCardSoldOverlay show={listing.is_sold} />
              {token ? (
                <ListingFavoriteButton
                  active={has(listing.id)}
                  label={has(listing.id) ? t('vc.favorites.remove') : t('vc.favorites.add')}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(listing.id)
                  }}
                />
              ) : null}
              {isOwnListing(listing) && <span className="listing-own-badge">{t('catalog.yourListing')}</span>}
              {isListingReserved(listing) ? (
                <span className="reserved-badge listing-card-cover__reserved">{t('listing.reservedBadge')}</span>
              ) : null}
              <button
                type="button"
                className={`listing-compare-btn${hasCompare(listing.id) ? ' listing-compare-btn--on' : ''}`}
                aria-label={hasCompare(listing.id) ? t('compare.remove') : t('compare.add')}
                aria-pressed={hasCompare(listing.id)}
                onClick={(e) => {
                  e.stopPropagation()
                  const r = toggleCompare(listing.id)
                  if (r === 'full') alert(t('compare.maxReached', { max: maxCompare }))
                }}
              >
                <span aria-hidden="true">⇄</span>
                <span className="listing-compare-btn__label">
                  {hasCompare(listing.id) ? t('compare.inCompare') : t('compare.toggleShort')}
                </span>
              </button>
            </div>
            <div className="listing-card-body">
              <a
                className="listing-card-hit"
                href={listingPublicPath(listing.id, i18n.language)}
                onMouseEnter={() => prefetchListing(listing.id)}
                onPointerDown={() => prefetchListing(listing.id)}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                  e.preventDefault()
                  onOpenListingDetail?.(listing.id)
                }}
              >
              <ListingCardSellerLine seller={listing.seller} sellerCity={listing.seller_city} />
              <h3>{listing.phone_model.manufacturer}</h3>
              <p className="model-name">{listing.phone_model.model_name}</p>
              <p className="title">{listing.title}</p>
              <div className="listing-card-extra">
                {listing.created_at ? (
                  <time className="listing-card-date" dateTime={listing.created_at}>
                    {formatListingPublishedAt(listing.created_at, i18n.language)}
                  </time>
                ) : null}
                {listing.in_box_completeness ? (
                  <p className="listing-kit-badge">{t(inBoxLabelKey(listing.in_box_completeness))}</p>
                ) : null}
                {listing.usage_duration ? (
                  <p className="listing-usage-badge">
                    {t('listing.usageBadge', {
                      duration: t(`listing.usage.${listing.usage_duration}`, listing.usage_duration),
                    })}
                  </p>
                ) : null}
              </div>
              <div className="listing-card-meta">
                <div className="listing-card-price-row">
                  <p className="price">€{listing.price.toFixed(2)}</p>
                  {isPlatformVerifiedListing(listing) ? <ListingCardVerifiedBadge /> : null}
                </div>
                <div className="listing-card-badges">
                  {!isPlatformVerifiedListing(listing) && listing.imei_verified ? (
                    <span className="trust-verified-badge">{t('trust.card.verified')}</span>
                  ) : null}
                  <span className={`warranty-badge${listing.has_factory_warranty ? ' warranty-badge--yes' : ''}`}>
                    {listing.has_factory_warranty
                      ? listing.warranty_remaining
                        ? t('listing.warrantyRemainingBadge', {
                            left: t(`listing.warrantyRemaining.${listing.warranty_remaining}`, listing.warranty_remaining),
                          })
                        : t('listing.warrantyBadgeYes')
                      : t('listing.warrantyBadgeNo')}
                  </span>
                  <ListingCardBatteryBadge health={listing.battery_health} />
                </div>
              </div>
              </a>
              {listing.is_sold ? (
                <p className="listing-own-hint">{t('listing.detailSold')}</p>
              ) : isOwnBlocked(listing) ? (
                <p className="listing-own-hint">{t('catalog.cannotOfferOwn')}</p>
              ) : reservationBlocksBuyer(listing, currentUserId) ? (
                <p className="listing-own-hint">{t('listing.detailReservedOther')}</p>
              ) : (
                <div className="listing-card-actions listing-card-actions--simple">
                  <button
                    type="button"
                    className="listing-card-btn listing-card-btn--buy listing-card-btn--buy-hero"
                    onClick={() => onOpenListingDetail?.(listing.id)}
                  >
                    {t('catalog.viewListing')}
                  </button>
                  <button
                    type="button"
                    className="listing-card-btn listing-card-btn--ghost"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!token) {
                        onRequireLogin?.('offer', listing.id)
                        return
                      }
                      setSelectedListing(listing)
                    }}
                  >
                    {t('catalog.offerShort')}
                  </button>
                </div>
              )}
            </div>
          </article>
          )
        })}
      </div>

      {selectedListing && token && (
        <OfferModal
          listingId={selectedListing.id}
          currentPrice={selectedListing.price}
          token={token}
          isOpen={Boolean(selectedListing)}
          onClose={() => setSelectedListing(null)}
          overlayClassName="modal-overlay--stack-top"
        />
      )}

      {exchangeListing && token && (
        <ExchangeOfferModal
          listingId={exchangeListing.id}
          currentPrice={exchangeListing.price}
          isOpen={Boolean(exchangeListing)}
          token={token}
          onClose={() => setExchangeListing(null)}
          overlayClassName="modal-overlay--stack-top"
          onGoToAccount={onGoToAccount}
        />
      )}

      <CompareBar onOpen={() => setCompareOpen(true)} />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        onOpenListing={(id) => { setCompareOpen(false); onOpenListingDetail?.(id) }}
      />
    </section>
  )
}
